/**
 * ContactFormModal.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getEmptyContactFormState, getContactFormDefaultsFromUser } from '../utils/contactFormDefaults';
import { useLanguage } from '../context/LanguageContext';
import './ContactFormModal.css';

const ContactFormModal = ({ isOpen, onClose }) => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const ui = t.ui.contact;

  const [formData, setFormData] = useState(() => getEmptyContactFormState(null));

  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: null
  });

  // When the modal opens, reset errors and apply name/email for the current session (once per open).
  useEffect(() => {
    if (!isOpen) return;
    setFormData(getEmptyContactFormState(currentUser));
    setStatus({ submitting: false, submitted: false, error: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when overlay opens; avoids wiping success if currentUser refreshes
  }, [isOpen]);

  // If the user signs in (or /me loads) while the modal is open, fill empty name/email fields.
  useEffect(() => {
    if (!isOpen || !currentUser) return;
    const d = getContactFormDefaultsFromUser(currentUser);
    setFormData((prev) => ({
      ...prev,
      ...(!prev.name?.trim() && d.name ? { name: d.name } : {}),
      ...(!prev.email?.trim() && d.email ? { email: d.email } : {}),
    }));
  }, [isOpen, currentUser]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset status
    setStatus({
      submitting: true,
      submitted: false,
      error: null
    });

    try {
      const response = await fetch('/api/contact/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.details || data.error || ui.errSendFailed);
      }

      // Success
      setStatus({
        submitting: false,
        submitted: true,
        error: null
      });

      // Reset form (keep profile name/email when signed in)
      setFormData(getEmptyContactFormState(currentUser));

    } catch (error) {
      console.error('Contact form error:', error);
      setStatus({
        submitting: false,
        submitted: false,
        error: error.message || ui.errSendFailed
      });
    }
  };

  const handleClose = () => {
    if (!status.submitting) {
      setStatus({ submitting: false, submitted: false, error: null });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="contact-modal-overlay" onClick={handleClose}>
      <div className="contact-modal-content" onClick={(e) => e.stopPropagation()}>
        <button 
          className="contact-modal-close"
          onClick={handleClose}
          disabled={status.submitting}
          aria-label={ui.closeAria}
        >
          ×
        </button>

        {status.submitted ? (
          <div className="contact-modal-success">
            <div className="success-icon">✓</div>
            <h3>{ui.successTitle}</h3>
            <p>{ui.successText}</p>
            <div className="modal-button-group">
              <button 
                className="btn-send-another"
                onClick={() => {
                  setFormData(getEmptyContactFormState(currentUser));
                  setStatus({ submitting: false, submitted: false, error: null });
                }}
              >
                {ui.sendAnother}
              </button>
              <button 
                className="btn-close-modal"
                onClick={handleClose}
              >
                {t.ui.common.close}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="contact-modal-header">
              <h2>{ui.title}</h2>
              <p className="modal-intro">{ui.intro}</p>
            </div>

            {status.error && (
              <div className="contact-form-error">
                <strong>{ui.errorPrefix}</strong> {status.error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="contact-modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="modal-name">
                    {ui.name} <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="modal-name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={ui.namePlaceholder}
                    required
                    disabled={status.submitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="modal-email">
                    {ui.email} <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="modal-email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={ui.emailPlaceholder}
                    required
                    disabled={status.submitting}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="modal-inquiryType">{ui.inquiryType}</label>
                  <select
                    id="modal-inquiryType"
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleChange}
                    disabled={status.submitting}
                  >
                    <option value="general">{ui.inqGeneral}</option>
                    <option value="order">{ui.inqOrder}</option>
                    <option value="product">{ui.inqProduct}</option>
                    <option value="wholesale">{ui.inqWholesale}</option>
                    <option value="technical">{ui.inqTechnical}</option>
                    <option value="feedback">{ui.inqFeedback}</option>
                    <option value="other">{ui.inqOther}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="modal-subject">
                    {ui.subject} <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="modal-subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder={ui.subjectPlaceholder}
                    required
                    disabled={status.submitting}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="modal-message">
                  {ui.message} <span className="required">*</span>
                </label>
                <textarea
                  id="modal-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={ui.messagePlaceholder}
                  rows="5"
                  required
                  disabled={status.submitting}
                />
              </div>

              <div className="modal-button-group">
                <button 
                  type="submit" 
                  className="btn-submit"
                  disabled={status.submitting}
                >
                  {status.submitting ? t.ui.common.sending : ui.send}
                </button>
                <button 
                  type="button"
                  className="btn-cancel"
                  onClick={handleClose}
                  disabled={status.submitting}
                >
                  {t.ui.common.cancel}
                </button>
              </div>
            </form>

            <div className="contact-modal-footer">
              <p>
                <strong>{ui.directEmail}</strong> <a href="mailto:cranfordpub@gmail.com">cranfordpub@gmail.com</a>
              </p>
              <p>
                <strong>{ui.responseTime}</strong> {ui.responseTimeValue}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ContactFormModal;

